import {
  CategoryID,
  SearchResult,
  type AdItem as AdItemType,
} from '@sbt-web/network/types';
import {
  GeoLevel,
  getGeoInclusionLabel,
  getGeoLevel,
  getLabelFromGeo,
} from '@sbt-web/networking';
import React, { useEffect } from 'react';
import { connect } from 'react-redux';
import { Button, Icon } from '@sbt-web/ui';
import { type SearchState, updateSearch } from '@reducers/search';
import type { RootState } from '@reducers/index';
import { adTypesLabelsMap } from '@tools/search/values';
import { useSavedSearches } from '@shared/helpers/SavedSearches';
import { AdItem } from '../../Containers/AdItem';
import ZrpImage from '../ZrpImage';
import { getDataLayer, getOrCreatePulse } from '@tools/tracking/utils';

import classes from './index.module.scss';
import { getAvailableFilters } from '@tools/tracking/getAvailableFiltersHelper';
import { transformSearch } from '@tools/tracking/transformer';
import { type FiltersConfigState } from '@reducers/filtersConfig';

type DispatchProps = {
  goToExtendedSearch: (extendedSearch: SearchState) => void;
};

type OwnProps = {
  extendedSearch: SearchState;
  extendedSearchData: SearchResult;
  listingCategoryId: CategoryID;
  enableOverrideUrgentForPro: boolean;
};

type StateProps = {
  search: SearchState;
  filtersConfig: FiltersConfigState;
};

type Props = OwnProps & StateProps & DispatchProps;

export const ZrpExtendedSearchComponent = ({
  extendedSearch,
  extendedSearchData,
  listingCategoryId,
  search,
  filtersConfig,
  goToExtendedSearch,
  enableOverrideUrgentForPro,
}: Props) => {
  const savedSearchesProvider = useSavedSearches();
  const { query, category, adType, geo, searchNearRegions } = search;
  const adTypeLabel = adTypesLabelsMap[adType]?.toLowerCase();

  const geoLabel = getLabelFromGeo(geo, searchNearRegions);
  const geoLevel = getGeoLevel(geo, searchNearRegions);

  const expandedSearchCount = extendedSearchData.ads.length;

  const handleVediTutti = () => {
    trackVediTutti(search.id);
    goToExtendedSearch(extendedSearch);
  };

  const handleAvvisami = () => {
    savedSearchesProvider.saveSearch(search, filtersConfig);
  };

  useEffect(() => {
    trackDisplayedAds(extendedSearch, filtersConfig, extendedSearchData.ads);
  }, [extendedSearch, filtersConfig, extendedSearchData.ads]);

  return (
    <>
      <div className={classes.container}>
        <ZrpImage className={classes.illustration} />
        <div className={classes.content}>
          <h6 className="headline-6">Nessun risultato al momento...</h6>
          <div className={classes.message}>
            {query && (
              <>
                <span className="body-text small">Stai cercando </span>
                <span className="caption">{query} </span>
                <span className="body-text small">in </span>
              </>
            )}
            <span className="caption">{`${category.label} ${adTypeLabel} `}</span>
            <span className="body-text small">
              {getGeoInclusionLabel(geoLevel ?? GeoLevel.Italia)}
            </span>
            <span className="caption">{` ${geoLabel}`}</span>.
          </div>
          <Button
            type="button"
            icon={<Icon name="Bell" />}
            onClick={handleAvvisami}
          >
            Avvisami
          </Button>
        </div>
      </div>

      <section className={`caption ${classes.count}`}>
        {expandedSearchCount} risultat{expandedSearchCount === 1 ? 'o ' : 'i '}
        in Tutta Italia
      </section>

      <section>
        {extendedSearchData.ads.map((item, index) => (
          <AdItem
            key={item.urn}
            item={item}
            index={index}
            listingCategory={listingCategoryId}
            enableOverrideUrgentForPro={enableOverrideUrgentForPro}
          />
        ))}
      </section>

      <section className={classes.cta}>
        <Button type="button" size="large" onClick={handleVediTutti}>
          Vedi tutti gli annunci
        </Button>
      </section>
    </>
  );
};

export default connect<StateProps, DispatchProps, OwnProps, RootState>(
  (state) => ({
    search: state.search,
    filtersConfig: state.filtersConfig,
  }),
  (dispatch) => ({
    goToExtendedSearch: (extendedSearch: SearchState) => {
      dispatch(updateSearch(extendedSearch));
    },
  })
)(ZrpExtendedSearchComponent);

function trackVediTutti(listingId?: string | null) {
  getOrCreatePulse()?.queueEvent({
    type: 'Engagement',
    object: {
      id: 'expand_all_ads_button_search_without_geo',
      type: 'UIElement',
      name: 'Vedi Tutti ZRP',
    },
    search: {
      '@id': listingId,
    },
    target: {
      id: listingId,
      type: 'Listing',
    },
  });
}

function trackDisplayedAds(
  searchState: SearchState,
  filtersConfigState: FiltersConfigState,
  ads: AdItemType[]
) {
  const { id: searchId, filterOrigin } = searchState;

  if (!searchId) return;

  const rawData = getDataLayer();
  const availableFilters = getAvailableFilters(
    searchState,
    filtersConfigState,
    rawData.search,
    ads.length
  );

  const searchMetadata = transformSearch(
    searchState,
    ads,
    searchId,
    filterOrigin,
    availableFilters
  );

  getOrCreatePulse()?.queueEvent({
    type: 'Recommend',
    object: {
      id: 'zrp_search_without_geo',
      type: 'Listing',
    },
    search: { ...searchMetadata, queryOrigin: 'Suggested' },
  });
}
